import { motion } from 'framer-motion';
import { Save, Printer, X, Loader2, Check } from 'lucide-react';

export interface StickyAction {
  label: string;
  icon?: React.ElementType;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  shortcut?: string;
}

interface StickyActionBarProps {
  actions: StickyAction[];
  /** If true the bar renders as a thin strip at the absolute bottom of a relative-positioned parent */
  inline?: boolean;
  className?: string;
}

const VARIANT_CLASSES: Record<NonNullable<StickyAction['variant']>, string> = {
  primary:   'bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30',
  secondary: 'bg-slate-700/60 text-slate-300 border border-slate-600/50 hover:bg-slate-700',
  danger:    'bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20',
  ghost:     'bg-transparent text-slate-500 border border-transparent hover:bg-slate-800/50 hover:text-slate-300',
};

export function StickyActionBar({ actions, inline = false, className = '' }: StickyActionBarProps) {
  if (inline) {
    return (
      <div className={`flex items-center justify-end gap-2 py-3 ${className}`}>
        {actions.map((a, i) => (
          <ActionButton key={i} action={a} />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.3 }}
      className={`fixed bottom-6 right-6 z-[150] flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-2xl border border-slate-800/60 backdrop-blur-xl ${className}`}
      style={{ background: 'rgba(var(--rb-surface-rgb), 0.92)' }}
    >
      {actions.map((a, i) => (
        <ActionButton key={i} action={a} />
      ))}
    </motion.div>
  );
}

function ActionButton({ action }: { action: StickyAction }) {
  const variant = action.variant ?? 'secondary';
  const Icon = action.loading ? Loader2 : (action.icon ?? Save);

  return (
    <button
      onClick={action.onClick}
      disabled={action.disabled || action.loading}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]}`}
    >
      <Icon className={`w-3.5 h-3.5 ${action.loading ? 'animate-spin' : ''}`} />
      {action.label}
      {action.shortcut && (
        <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/30 opacity-60">
          {action.shortcut}
        </kbd>
      )}
    </button>
  );
}

// ─── Pre-built common action factories ───────────────────────────────────────

export function makeSaveAction(onSave: () => void, saving?: boolean): StickyAction {
  return {
    label: saving ? 'Saving…' : 'Save',
    icon: saving ? Loader2 : Save,
    onClick: onSave,
    variant: 'primary',
    loading: saving,
    shortcut: 'Ctrl+S',
  };
}

export function makePrintAction(onPrint?: () => void): StickyAction {
  return {
    label: 'Print',
    icon: Printer,
    onClick: onPrint ?? (() => window.print()),
    variant: 'secondary',
    shortcut: 'Ctrl+P',
  };
}

export function makeCancelAction(onCancel: () => void): StickyAction {
  return {
    label: 'Cancel',
    icon: X,
    onClick: onCancel,
    variant: 'ghost',
  };
}
